import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  TrendingUp,
  People,
  AccountBalance,
  Refresh,
  ArrowForward,
  CalendarToday,
  Assessment,
  Email,
  Close,
  AttachMoney,
  AccountBalanceWallet,
  TrendingDown,
  CallMade,
  CallReceived,
  Description
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { DashboardMetrics, OutstandingSummary, Reminder } from '../../models/LendingModels';
import { mockDashboardMetrics, mockOutstandingSummaries, mockReminders, mockCustomers } from '../../data/MockData';
import { formatCurrency } from '../../utils/CurrencyFormat';
import { getInsights, getMonthlyOutstandingReport, sendMonthlyOutstandingReportEmail, getLendingClassification, getCurrentYearCashflow, sendAccountStatementEmail, generateMonthlyReport, sendMonthlyReportEmail, getShortTermDebitsDueThisMonth } from '../../services/request/Dashboard';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LendingDashboard: React.FC = () => {
  const [metrics] = useState<DashboardMetrics>(mockDashboardMetrics);
  const [outstandingSummaries] = useState<OutstandingSummary[]>(mockOutstandingSummaries);
  const [reminders] = useState<Reminder[]>(mockReminders);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState<{ 
    totalOutstanding: number; 
    customerCount: number;
    interestExpectedCurrentMonth: number;
    interestCollectedCurrentMonth: number;
    totalActiveInterestOnDebits: number;
    totalInterestEarnedTillNow: number;
    interestInsights: Array<{
      paymentDate: number;
      interest: number;
      borrowerId: string;
      name: string;
      debitId: string;
      debitStartDate?: string;
    }>;
  }>({
    totalOutstanding: 0,
    customerCount: 0,
    interestExpectedCurrentMonth: 0,
    interestCollectedCurrentMonth: 0,
    totalActiveInterestOnDebits: 0,
    totalInterestEarnedTillNow: 0,
    interestInsights: []
  });
  const [error, setError] = useState<string | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingAccountStatement, setSendingAccountStatement] = useState(false);
  const [sendingMonthlyReport, setSendingMonthlyReport] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [lendingClassification, setLendingClassification] = useState<{
    interestBasedLendingTotal: number;
    shortTermLendingTotal: number;
    totalLending: number;
    interestBasedLendingPercentage: number;
    shortTermLendingPercentage: number;
  } | null>(null);
  const [loadingClassification, setLoadingClassification] = useState(false);
  const [cashflowData, setCashflowData] = useState<{
    year: number;
    principalCollected: number;
    interestCollected: number;
    lateFeesCollected: number;
    totalCashInflow: number;
    newLoanDisbursements: number;
    totalCashOutflow: number;
    netCashFlow: number;
    totalInterestEarnedInYear: number;
  } | null>(null);
  const [loadingCashflow, setLoadingCashflow] = useState(false);
  const [shortTermDebitsDueThisMonth, setShortTermDebitsDueThisMonth] = useState<Array<{
    id: string;
    borrowerId: string;
    borrowerName?: string;
    amount: number;
    outstanding: number;
    startDate?: string;
    endDate?: string;
    status: string;
  }>>([]);

  // Mock data for charts
  const monthlyTrendData = [
    { month: 'Jan', outstanding: 85000, interest: 1200 },
    { month: 'Feb', outstanding: 87000, interest: 1350 },
    { month: 'Mar', outstanding: 88000, interest: 1400 },
    { month: 'Apr', outstanding: 88051, interest: 1450 }
  ];

  // Lending classification data for pie chart
  const lendingClassificationData = lendingClassification ? [
    { 
      name: 'Interest-Based Lending', 
      value: lendingClassification.interestBasedLendingTotal, 
      percentage: lendingClassification.interestBasedLendingPercentage,
      color: '#8884d8' 
    },
    { 
      name: 'Short-Term Lending', 
      value: lendingClassification.shortTermLendingTotal, 
      percentage: lendingClassification.shortTermLendingPercentage,
      color: '#82ca9d' 
    }
  ] : [];

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getInsights({});
      
      console.log('Dashboard API response:', response);
      
      if (response.data && response.data.totalOutstanding !== undefined) {
        setDashboardData({
          totalOutstanding: response.data.totalOutstanding,
          customerCount: response.data.customerCount,
          interestExpectedCurrentMonth: response.data.interestExpectedCurrentMonth || 0,
          interestCollectedCurrentMonth: response.data.interestCollectedCurrentMonth || 0,
          totalActiveInterestOnDebits: response.data.totalActiveInterestOnDebits || 0,
          totalInterestEarnedTillNow: response.data.totalInterestEarnedTillNow || 0,
          interestInsights: [...(response.data.interestInsights || [])].sort(
            (a, b) => a.paymentDate - b.paymentDate
          )
        });
      } else if (response.totalOutstanding !== undefined) {
        setDashboardData({
          totalOutstanding: response.totalOutstanding,
          customerCount: response.customerCount,
          interestExpectedCurrentMonth: response.interestExpectedCurrentMonth || 0,
          interestCollectedCurrentMonth: response.interestCollectedCurrentMonth || 0,
          totalActiveInterestOnDebits: response.totalActiveInterestOnDebits || 0,
          totalInterestEarnedTillNow: response.totalInterestEarnedTillNow || 0,
          interestInsights: [...(response.interestInsights || [])].sort(
            (a, b) => a.paymentDate - b.paymentDate
          )
        });
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLendingClassification = async () => {
    try {
      setLoadingClassification(true);
      const response = await getLendingClassification();
      
      console.log('Lending Classification API response:', response);
      
      if (response.data) {
        setLendingClassification({
          interestBasedLendingTotal: response.data.interestBasedLendingTotal || 0,
          shortTermLendingTotal: response.data.shortTermLendingTotal || 0,
          totalLending: response.data.totalLending || 0,
          interestBasedLendingPercentage: response.data.interestBasedLendingPercentage || 0,
          shortTermLendingPercentage: response.data.shortTermLendingPercentage || 0,
        });
      } else if (response.interestBasedLendingTotal !== undefined) {
        setLendingClassification({
          interestBasedLendingTotal: response.interestBasedLendingTotal || 0,
          shortTermLendingTotal: response.shortTermLendingTotal || 0,
          totalLending: response.totalLending || 0,
          interestBasedLendingPercentage: response.interestBasedLendingPercentage || 0,
          shortTermLendingPercentage: response.shortTermLendingPercentage || 0,
        });
      }
    } catch (err) {
      console.error('Error fetching lending classification:', err);
      // Don't set error state here to avoid disrupting the main dashboard
    } finally {
      setLoadingClassification(false);
    }
  };

  const fetchCurrentYearCashflow = async () => {
    try {
      setLoadingCashflow(true);
      const response = await getCurrentYearCashflow();
      
      console.log('Current Year Cashflow API response:', response);
      
      if (response.data) {
        setCashflowData({
          year: response.data.year || new Date().getFullYear(),
          principalCollected: response.data.principalCollected || 0,
          interestCollected: response.data.interestCollected || 0,
          lateFeesCollected: response.data.lateFeesCollected || 0,
          totalCashInflow: response.data.totalCashInflow || 0,
          newLoanDisbursements: response.data.newLoanDisbursements || 0,
          totalCashOutflow: response.data.totalCashOutflow || 0,
          netCashFlow: response.data.netCashFlow || 0,
          totalInterestEarnedInYear: response.data.totalInterestEarnedInYear || 0,
        });
      } else if (response.year !== undefined) {
        setCashflowData({
          year: response.year || new Date().getFullYear(),
          principalCollected: response.principalCollected || 0,
          interestCollected: response.interestCollected || 0,
          lateFeesCollected: response.lateFeesCollected || 0,
          totalCashInflow: response.totalCashInflow || 0,
          newLoanDisbursements: response.newLoanDisbursements || 0,
          totalCashOutflow: response.totalCashOutflow || 0,
          netCashFlow: response.netCashFlow || 0,
          totalInterestEarnedInYear: response.totalInterestEarnedInYear || 0,
        });
      }
    } catch (err) {
      console.error('Error fetching current year cashflow:', err);
      // Don't set error state here to avoid disrupting the main dashboard
    } finally {
      setLoadingCashflow(false);
    }
  };

  const fetchShortTermDebitsDueThisMonth = async () => {
    try {
      const response = await getShortTermDebitsDueThisMonth();

      console.log('Short-term debits due this month response:', response);

      const data = response.data || response;
      const list = data.result || data.debits || data || [];
      setShortTermDebitsDueThisMonth(list);
    } catch (err) {
      console.error('Error fetching short-term debits due this month:', err);
      // Do not surface as global error; this is a supplementary widget
      setShortTermDebitsDueThisMonth([]);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchLendingClassification();
    fetchCurrentYearCashflow();
    fetchShortTermDebitsDueThisMonth();
  }, []);

  const handleRefresh = async () => {
    await Promise.all([
      fetchDashboardData(),
      fetchLendingClassification(),
      fetchCurrentYearCashflow(),
      fetchShortTermDebitsDueThisMonth()
    ]);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getReminderTypeColor = (type: string) => {
    switch (type) {
      case 'payment_due': return 'warning';
      case 'overdue': return 'error';
      case 'interest_accrual': return 'info';
      default: return 'default';
    }
  };

  const handleNavigateToCustomerDebits = (borrowerId: string) => {
    window.open(`/admin/customers/${borrowerId}/debits`, '_blank');
  };

  const handleFetchReport = async () => {
    try {
      setLoadingReport(true);
      const response = await getMonthlyOutstandingReport();
      
      console.log('Report response:', response);
      
      if (response.data) {
        setReportData(response.data);
        setReportDialogOpen(true);
        toast.success('Report fetched successfully!');
      } else if (response.statusCode === 200) {
        setReportData(response);
        setReportDialogOpen(true);
        toast.success('Report fetched successfully!');
      }
    } catch (err) {
      console.error('Error fetching report:', err);
      toast.error('Failed to fetch report');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleSendReportEmail = async () => {
    try {
      setSendingEmail(true);
      const response = await sendMonthlyOutstandingReportEmail();
      
      console.log('Email response:', response);
      
      if (response.data?.code === 200 || response.code === 200 || response.data?.statusCode === 200 || response.statusCode === 200) {
        const message = response.data?.message || response.message || 'Report email sent successfully!';
        toast.success(`📧 ${message}`);
      } else {
        toast.error('Failed to send report email');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error('Failed to send report email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setReportData(null);
  };

  const handleSendAccountStatementEmail = async () => {
    try {
      setSendingAccountStatement(true);
      const response = await sendAccountStatementEmail();
      
      console.log('Account Statement Email response:', response);
      
      if (response.data?.code === 200 || response.code === 200 || response.data?.statusCode === 200 || response.statusCode === 200) {
        const message = response.data?.message || response.message || 'Account statement email sent successfully!';
        toast.success(`📧 ${message}`);
      } else {
        toast.error('Failed to send account statement email');
      }
    } catch (err) {
      console.error('Error sending account statement email:', err);
      toast.error('Failed to send account statement email');
    } finally {
      setSendingAccountStatement(false);
    }
  };

  const handleGenerateMonthlyReport = async () => {
    try {
      setGeneratingReport(true);
      const response = await generateMonthlyReport();
      
      console.log('Monthly Report response:', response);
      
      if (response && (response.statusCode === 200 || response.code === 200)) {
        toast.success('📊 Monthly report generated successfully!');
        // Optionally show report data in a dialog
      } else {
        toast.error('Failed to generate monthly report');
      }
    } catch (err) {
      console.error('Error generating monthly report:', err);
      toast.error('Failed to generate monthly report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleSendMonthlyReportEmail = async () => {
    try {
      setSendingMonthlyReport(true);
      const response = await sendMonthlyReportEmail();
      
      console.log('Monthly Report Email response:', response);
      
      if (response && (response.statusCode === 200 || response.code === 200)) {
        const message = response.message || response.data || 'Monthly report email sent successfully!';
        toast.success(`📧 ${message}`);
      } else {
        toast.error('Failed to send monthly report email');
      }
    } catch (err) {
      console.error('Error sending monthly report email:', err);
      toast.error('Failed to send monthly report email');
    } finally {
      setSendingMonthlyReport(false);
    }
  };

  const isPaymentDueToday = (paymentDate: number) => {
    const today = new Date();
    const currentDay = today.getDate();
    return currentDay === paymentDate;
  };

  return (
    <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2, mt: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Lending Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Assessment />}
            onClick={handleFetchReport}
            disabled={loadingReport}
          >
            {loadingReport ? 'Loading...' : 'View Report'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Email />}
            onClick={handleSendReportEmail}
            disabled={sendingEmail}
          >
            {sendingEmail ? 'Sending...' : 'Email Report'}
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<Description />}
            onClick={handleSendAccountStatementEmail}
            disabled={sendingAccountStatement}
          >
            {sendingAccountStatement ? 'Sending...' : 'Send Statement'}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Assessment />}
            onClick={handleGenerateMonthlyReport}
            disabled={generatingReport}
          >
            {generatingReport ? 'Generating...' : 'Generate Report'}
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<Email />}
            onClick={handleSendMonthlyReportEmail}
            disabled={sendingMonthlyReport}
          >
            {sendingMonthlyReport ? 'Sending...' : 'Send Monthly Report'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Total Interest Earned - Highlighted Glassy Pane */}
      <Box
        sx={{
          mb: 4,
          position: 'relative',
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(33, 150, 243, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #4caf50 0%, #2196f3 50%, #4caf50 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': {
              backgroundPosition: '200% 0',
            },
            '100%': {
              backgroundPosition: '-200% 0',
            },
          },
        }}
      >
        <Box
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(33, 150, 243, 0.2) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AttachMoney sx={{ fontSize: 40, color: '#4caf50' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5, color: 'text.primary', fontWeight: 500 }}>
                Total Interest Earned Till Now
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cumulative interest collected from all transactions
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #4caf50 0%, #2196f3 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: 1.2,
              }}
            >
              {formatCurrency(dashboardData.totalInterestEarnedTillNow)}
            </Typography>
            <Chip
              label="Total Earned"
              size="small"
              sx={{
                mt: 1,
                background: 'rgba(76, 175, 80, 0.2)',
                color: '#4caf50',
                fontWeight: 'bold',
                border: '1px solid rgba(76, 175, 80, 0.3)',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalance color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Outstanding</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {formatCurrency(dashboardData.totalOutstanding)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                From API data
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Customers</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {dashboardData.customerCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active customer count
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Interest Expected</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {formatCurrency(dashboardData.interestExpectedCurrentMonth)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expected this month
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Interest Collected</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {formatCurrency(dashboardData.interestCollectedCurrentMonth)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Collected this month
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Maximum Interest Possible</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {formatCurrency(dashboardData.totalActiveInterestOnDebits)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total active interest on debits
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Interest Insights Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Interest Payment Insights
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Upcoming interest payments scheduled by date
            </Typography>
          </Box>
        </Box>
        
        {dashboardData.interestInsights.length === 0 ? (
          <Alert severity="info" sx={{ py: 1 }}>
            No interest insights available for this month.
          </Alert>
        ) : (
          <Grid container spacing={1.5}>
            {dashboardData.interestInsights.map((insight, index) => {
              const isDueToday = isPaymentDueToday(insight.paymentDate);
              return (
                <Grid item xs={12} sm={6} md={4} key={`${insight.borrowerId}-${insight.paymentDate}-${index}`}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      height: '100%',
                      transition: 'all 0.3s',
                      border: isDueToday ? '2px solid' : '1px solid',
                      borderColor: isDueToday ? 'warning.main' : 'divider',
                      bgcolor: isDueToday ? 'rgba(255, 152, 0, 0.08)' : 'background.paper',
                      boxShadow: isDueToday ? 2 : 0,
                      '&:hover': {
                        boxShadow: isDueToday ? 3 : 1,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CalendarToday 
                        color={isDueToday ? "warning" : "primary"} 
                        sx={{ mr: 0.75, fontSize: '1.1rem' }} 
                      />
                      <Typography variant="subtitle2" color={isDueToday ? "warning.dark" : "primary"} sx={{ fontWeight: 600 }}>
                        Day {insight.paymentDate}
                        {isDueToday && (
                          <Chip 
                            label="DUE TODAY" 
                            size="small" 
                            color="warning" 
                            sx={{ ml: 0.75, height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                          />
                        )}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                        Interest Payment
                      </Typography>
                      <Typography variant="h6" color="success.main" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                        {formatCurrency(insight.interest)}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                        Borrower
                      </Typography>
                      <Typography variant="body2" fontWeight="600" color="primary" sx={{ lineHeight: 1.2 }}>
                        {insight.name || 'Unknown'}
                      </Typography>
                    </Box>

                    {insight.debitStartDate && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                          Debit Start Date
                        </Typography>
                        <Typography variant="body2" fontWeight="500" color="text.primary" sx={{ lineHeight: 1.2 }}>
                          {new Date(insight.debitStartDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    )}

                    <Button
                      fullWidth
                      variant={isDueToday ? "contained" : "outlined"}
                      color={isDueToday ? "warning" : "primary"}
                      endIcon={<ArrowForward sx={{ fontSize: '1rem' }} />}
                      onClick={() => handleNavigateToCustomerDebits(insight.borrowerId)}
                      size="small"
                      sx={{
                        mt: 0.5,
                        py: 0.5,
                        fontSize: '0.75rem',
                        fontWeight: isDueToday ? 'bold' : 'normal',
                        minHeight: 32,
                        ...(isDueToday && {
                          '&:hover': {
                            bgcolor: 'warning.dark',
                            color: 'white'
                          }
                        })
                      }}
                    >
                      {isDueToday ? 'Collect Payment Now' : 'View Debits'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {/* Current Year Cashflow Widget - Glassy Effect */}
      {loadingCashflow ? (
        <Box sx={{ mb: 4 }}>
          <LinearProgress />
        </Box>
      ) : cashflowData && (
        <Box
          sx={{
            mb: 4,
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.15) 0%, rgba(63, 81, 181, 0.15) 100%)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #9c27b0 0%, #3f51b5 50%, #9c27b0 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 3s ease-in-out infinite',
            },
            '@keyframes shimmer': {
              '0%': {
                backgroundPosition: '200% 0',
              },
              '100%': {
                backgroundPosition: '-200% 0',
              },
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(63, 81, 181, 0.2) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AccountBalanceWallet sx={{ fontSize: 32, color: '#9c27b0' }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Current Year Cashflow
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Year {cashflowData.year} - Complete Financial Overview
                  </Typography>
                </Box>
              </Box>
              <Chip
                label={`Net: ${formatCurrency(cashflowData.netCashFlow)}`}
                size="medium"
                icon={cashflowData.netCashFlow >= 0 ? <TrendingUp /> : <TrendingDown />}
                color={cashflowData.netCashFlow >= 0 ? 'success' : 'error'}
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  background: cashflowData.netCashFlow >= 0 
                    ? 'rgba(76, 175, 80, 0.2)' 
                    : 'rgba(244, 67, 54, 0.2)',
                  border: `1px solid ${cashflowData.netCashFlow >= 0 ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                }}
              />
            </Box>

            {/* Cashflow Details Grid */}
            <Grid container spacing={2}>
              {/* Cash Inflow Section */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    background: 'rgba(76, 175, 80, 0.1)',
                    border: '1px solid rgba(76, 175, 80, 0.2)',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CallReceived sx={{ color: '#4caf50', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                      Cash Inflow
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Principal Collected
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(cashflowData.principalCollected)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Interest Collected
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(cashflowData.interestCollected)}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Late Fees Collected
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(cashflowData.lateFeesCollected)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      pt: 2,
                      borderTop: '2px solid rgba(76, 175, 80, 0.3)',
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Total Cash Inflow
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {formatCurrency(cashflowData.totalCashInflow)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Cash Outflow Section */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    background: 'rgba(244, 67, 54, 0.1)',
                    border: '1px solid rgba(244, 67, 54, 0.2)',
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CallMade sx={{ color: '#f44336', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336' }}>
                      Cash Outflow
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      New Loan Disbursements
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {formatCurrency(cashflowData.newLoanDisbursements)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      pt: 2,
                      borderTop: '2px solid rgba(244, 67, 54, 0.3)',
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Total Cash Outflow
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                      }}
                    >
                      {formatCurrency(cashflowData.totalCashOutflow)}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px dashed rgba(244, 67, 54, 0.2)' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Total Interest Earned (Year)
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                      {formatCurrency(cashflowData.totalInterestEarnedInYear)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}

      {/* Charts Row */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} lg={4} xl={3}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Lending Classification
            </Typography>
            {loadingClassification ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                <LinearProgress sx={{ width: '80%' }} />
              </Box>
            ) : lendingClassificationData.length > 0 ? (
              <>
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    {formatCurrency(lendingClassification?.totalLending || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Lending
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={lendingClassificationData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                    >
                      {lendingClassificationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      labelFormatter={(label) => label}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 1 }}>
                  {lendingClassificationData.map((entry, index) => (
                    <Box key={index} sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: entry.color }}>
                        {entry.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(entry.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                <Alert severity="info">No lending classification data available</Alert>
              </Box>
            )}
          </Paper>
        </Grid>
        {/* Short-Term Debits Due This Month */}
        <Grid item xs={12} lg={8} xl={9}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Short-Term Debits Due This Month
            </Typography>
            {shortTermDebitsDueThisMonth.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                <Alert severity="info">
                  No short-term debits are due this month.
                </Alert>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shortTermDebitsDueThisMonth.map((debit) => (
                      <TableRow key={debit.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {debit.borrowerName || 'Unknown Customer'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', display: 'block' }}
                          >
                            {debit.borrowerId?.substring(0, 10)}...
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {formatCurrency(debit.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="error">
                            {formatCurrency(debit.outstanding)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {debit.endDate
                            ? new Date(debit.endDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={debit.status || 'N/A'}
                            size="small"
                            color={
                              debit.status === 'ACTIVE'
                                ? 'success'
                                : debit.status === 'CLOSED'
                                ? 'default'
                                : 'warning'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<ArrowForward sx={{ fontSize: '1rem' }} />}
                            onClick={() => handleNavigateToCustomerDebits(debit.borrowerId)}
                          >
                            View Debits
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

     

      {/* Monthly Outstanding Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5">Monthly Outstanding Report</Typography>
              {reportData && (
                <Typography variant="body2" color="text.secondary">
                  Report Date: {reportData.reportDate || new Date().toLocaleDateString()}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseReportDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {reportData ? (
            <Box>
              {/* Summary Stats */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Customers
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {reportData.totalCustomers}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Outstanding Amount
                      </Typography>
                      <Typography variant="h4" color="error">
                        {formatCurrency(reportData.totalOutstandingAmount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Borrowers Table */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Borrower Details
              </Typography>
              {reportData.borrowers && reportData.borrowers.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Customer ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Borrower Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Borrower ID</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Outstanding</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.borrowers.map((borrower: any, index: number) => (
                        <TableRow key={borrower.borrowerId || index} hover>
                          <TableCell>{borrower.customerId || 'N/A'}</TableCell>
                          <TableCell>{borrower.borrowerName}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {borrower.borrowerId.substring(0, 12)}...
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {formatCurrency(borrower.totalOutstanding)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No borrower data available</Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No report data available</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportDialog}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<Email />}
            onClick={handleSendReportEmail}
            disabled={sendingEmail}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LendingDashboard;
